import { Button } from '@montel/montelpro-shared-components-v2';
import { ACCOUNT_API_GRANT_ACCESS, ACCOUNT_API_REVOKE_ACCESS } from 'apiRoutes';
import { ConfirmationModal } from 'components/common/ConfirmationModal/ConfirmationModal';
import {
	getProductAccessStatusName,
	ProductAccessStatus,
} from 'domain/product/enums';
import { ProductRegistration } from 'domain/product/interfaces';
import useAuthRoles from 'hooks/useAuthRoles';
import useSubmit, { POST } from 'hooks/useSubmit';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { RequestStatus } from 'types/RequestStatus';

interface ProductAccessButtonProps {
	productRegistration: ProductRegistration | undefined;
	userId: string;
	onSuccess: (productId: string, newStatus: ProductAccessStatus) => void;
}

export const ProductAccessButton: React.FunctionComponent<
	ProductAccessButtonProps
> = ({ productRegistration, userId, onSuccess }) => {
	if (!productRegistration) return <></>;

	const submitUrl =
		productRegistration.accessStatus === ProductAccessStatus.Allowed
			? `${ACCOUNT_API_REVOKE_ACCESS}`
			: `${ACCOUNT_API_GRANT_ACCESS}`;

	const [updateAccess, updateAccessStatus] = useSubmit(submitUrl, POST);
	const [showConfirmationModal, setShowConfirmationModal] =
		useState<boolean>(false);
	const [newStatus, setNewStatus] = useState<ProductAccessStatus>(
		ProductAccessStatus.Allowed,
	);

	const updateProductAccessStatus = async (comment?: string) => {
		const result = await updateAccess({
			accountId: userId,
			productId: productRegistration.productId,
			comment: comment,
		});
		if (result.success) {
			productRegistration.accessStatus = newStatus;
			productRegistration.reviewed = true;
			onSuccess(productRegistration.productId, newStatus);
			toast.success(
				`Access status for ${
					productRegistration.productName
				} was changed to ${getProductAccessStatusName(newStatus)}.`,
				{ autoClose: 2000 },
			);
		} else {
			toast.error('An error occurred, please try again!');
		}
	};

	const onClickHandler = async (
		event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		newStatus: ProductAccessStatus,
	) => {
		event.stopPropagation();
		event.preventDefault();
		setNewStatus(newStatus);
		setShowConfirmationModal(true);
	};

	const modalConfig = () =>
		productRegistration.accessStatus == ProductAccessStatus.Allowed
			? {
					header: 'Deny access',
					text: `Are you sure you want to deny access to ${productRegistration.productName}?`,
					buttonText: 'Deny',
			  }
			: {
					header: 'Allow access',
					text: `Are you sure you want to allow access to ${productRegistration.productName}?`,
					buttonText: 'Allow',
			  };

	return (
		<>
			<Action
				productRegistration={productRegistration}
				onClickHandler={onClickHandler}
				updateAccessStatus={updateAccessStatus}
			/>
			<ConfirmationModal
				showModal={showConfirmationModal}
				setShowModal={setShowConfirmationModal}
				onSubmit={updateProductAccessStatus}
				header={modalConfig().header}
				text={modalConfig().text}
				buttonText={modalConfig().buttonText}
				loadingStatus={RequestStatus.UNLOADED}
			/>
		</>
	);
};

export const Action: React.FunctionComponent<{
	productRegistration: ProductRegistration;
	onClickHandler: (
		event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		newStatus: ProductAccessStatus,
	) => void;
	updateAccessStatus: RequestStatus;
}> = ({ productRegistration, onClickHandler, updateAccessStatus }) => {
	const { canChangeProductAccess } = useAuthRoles();

	switch (productRegistration.accessStatus) {
		case ProductAccessStatus.Allowed:
			return (
				<Button
					text="Deny"
					size="sm"
					variant="negative"
					type="submit"
					onClick={async (
						event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
					) => onClickHandler(event, ProductAccessStatus.Denied)}
					isLoading={updateAccessStatus == RequestStatus.LOADING}
					loadingText="Updating..."
					isDisabled={
						!canChangeProductAccess(productRegistration.productId)
					}
				/>
			);
		case ProductAccessStatus.Denied:
			return (
				<Button
					text="Allow"
					size="sm"
					type="submit"
					onClick={(
						event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
					) => onClickHandler(event, ProductAccessStatus.Allowed)}
					isLoading={updateAccessStatus == RequestStatus.LOADING}
					loadingText="Updating..."
					isDisabled={
						!canChangeProductAccess(productRegistration.productId)
					}
				/>
			);
		default:
			return <>-</>;
	}
};
